import type { JSX } from 'react';
import { cn } from '@/lib/utils';
import { countryName, describePhone, flag, toE164 } from '@/lib/phone';
import { input } from '../shell/look';

/**
 * A phone number for any country, formatted as it is typed.
 *
 * The country is read from the calling code rather than picked from a list —
 * `+998 90…` is Uzbekistan, `+1 312…` is the US — because a Telegram account is
 * always entered in international form, and a dropdown of 240 countries is the
 * slowest possible way to type a number you already know.
 *
 * `value` is E.164 (`+998901234567`), which is what the API is sent; the spaces
 * are display only.
 */
export function PhoneInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (e164: string) => void;
}): JSX.Element {
  const { display, country, valid } = describePhone(value);

  return (
    <div>
      <div className="relative">
        {country !== undefined && (
          <span
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-lg"
            aria-hidden
          >
            {flag(country)}
          </span>
        )}
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={display}
          placeholder="+998 90 123 45 67"
          aria-invalid={value.length > 4 && !valid}
          className={cn(input, 'font-mono text-[13.5px]', country !== undefined && 'pl-10')}
          onChange={(event) => onChange(toE164(event.target.value))}
        />
      </div>
      <div className={cn('mt-1.5 text-xs', valid ? 'text-ok' : 'text-mute')}>
        {country === undefined
          ? 'Start with + and the country code.'
          : `${countryName(country)} · ${valid ? 'valid number' : 'keep typing'}`}
      </div>
    </div>
  );
}
